import "leaflet/dist/leaflet.css"
import LoginPage from "./login_form/LoginPage.js"
import React, { Component } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Link,
    Navigate,
    useNavigate,
} from "react-router-dom";
import {Button} from "@material-ui/core";
import PersonIcon from "@mui/icons-material/Person";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MapSwe from "./map/Map";
import MyPages from "./my_pages/MyPages.js";
import ListViewPage from "./map/ListViewPage";
import AdminGatePage from "./admin/AdminGatePage";
import FaqPage from "./faq/FaqPage";
import AreaPage from "./area/AreaPage";
import ComparePage from "./area/ComparePage";
import { ReactComponent as Logo } from './omradeskollen_logga2.svg';
import Cookies from 'universal-cookie';
import { initAnalytics, setAnalyticsContext, track } from './analytics/events.js';
import { markIntroSeen } from './map/IntroTips.js';
import { isPerfRun } from './perfRun.js';


// Renders the view for its route (listvy at `/`, kartvy at `/kartvy`) and turns a view switch into a
// navigation, so the URL always matches the view. On an admin hostname both routes show the admin gate, except
// for the admin panel's map speed test (`?perf`, perfRun.js), which opens the views in a frame there.
function AppViewRoute({ view, isAdminHost, languageState, darkMode, onViewChange }) {
    const navigate = useNavigate();
    if (isAdminHost && !isPerfRun()) return <AdminGatePage />;
    const handleSwitch = (nextMode) => {
        const mode = nextMode === 'kartvy' ? 'kartvy' : 'listvy';
        markIntroSeen('switch'); // found the way between the views: no "Lista eller karta" tip
        onViewChange(mode);
        navigate(mode === 'kartvy' ? '/kartvy' : '/');
    };
    const ViewComponent = view === 'kartvy' ? MapSwe : ListViewPage;
    return <ViewComponent languageState={languageState} darkMode={darkMode} onSwitchView={handleSwitch} />;
}

export default class App extends Component {
    constructor(props) {
        super(props);
        this.cookies = new Cookies();
        const savedLanguage = this.cookies.get('language_state');
        const isSwedish = savedLanguage !== 'eng';
        const savedThemeMode = this.cookies.get('theme_mode');
        const isDarkMode = savedThemeMode === 'dark';
        // The view is URL-driven: `/` is listvy (the default), `/kartvy` is kartvy. The root always opens
        // listvy, so a returning visitor doesn't land on a view a cookie remembered. `view_mode` is still
        // written on a switch for analytics continuity. (The old `map_view_mode` cookie is ignored.)
        const initialPath = typeof window !== 'undefined' ? window.location.pathname : '/';
        const initialViewMode = initialPath === '/kartvy' ? 'kartvy' : 'listvy';
        if (this.cookies.get('map_view_mode') !== undefined) this.cookies.remove('map_view_mode', { path: '/' });

        this.state = {
            sessionActive: false,
            fname: "",
            role_id: 0,
            selectedNavbar: "",
            betaVersion: true,
            darkMode: isDarkMode,
            viewMode: initialViewMode,
            languageState: {
                swedish: isSwedish,
                language: !isSwedish,
            },
        };
    }

    setLanguage = (isSwedish) => {
        this.setState({
            languageState: {
                swedish: isSwedish,
                language: !isSwedish,
            },
        });
        this.cookies.set('language_state', isSwedish ? 'sv' : 'eng', {
            path: '/',
            maxAge: 60 * 60 * 24 * 365,
        });
    };

    toggleLanguage = () => {
        const nextSwedish = !this.state.languageState.swedish;
        track('language.toggle', { to: nextSwedish ? 'sv' : 'en' });
        setAnalyticsContext({ lang: nextSwedish ? 'sv' : 'en' });
        this.setLanguage(nextSwedish);
    };

    setThemeMode = (isDarkMode) => {
        this.setState({ darkMode: isDarkMode });
        this.cookies.set('theme_mode', isDarkMode ? 'dark' : 'light', {
            path: '/',
            maxAge: 60 * 60 * 24 * 365,
        });
    };

    toggleThemeMode = () => {
        const nextDark = !this.state.darkMode;
        track('theme.toggle', { to: nextDark ? 'dark' : 'light' });
        setAnalyticsContext({ theme: nextDark ? 'dark' : 'light' });
        this.setThemeMode(nextDark);
    };

    setViewMode = (nextMode) => {
        const viewMode = nextMode === 'listvy' ? 'listvy' : 'kartvy';
        setAnalyticsContext({ view: viewMode });  // records view.switch and time spent in the previous view
        this.setState({ viewMode });
        this.cookies.set('view_mode', viewMode, {
            path: '/',
            maxAge: 60 * 60 * 24 * 365,
        });
    };

    componentDidMount() {
        this.syncBodyThemeClass(this.state.darkMode);
        // Visitor analytics for the public site only (not the admin pages, nor their map speed test).
        const hostname = String(window.location.hostname || '').toLowerCase();
        if (!hostname.startsWith('admin.') && !window.location.pathname.startsWith('/admin') && !isPerfRun()) {
            initAnalytics({
                view: this.state.viewMode,
                lang: this.state.languageState.swedish ? 'sv' : 'en',
                theme: this.state.darkMode ? 'dark' : 'light',
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.darkMode !== this.state.darkMode) {
            this.syncBodyThemeClass(this.state.darkMode);
        }
    }

    componentWillUnmount() {
        if (typeof document !== 'undefined') {
            document.body.classList.remove('theme-dark');
        }
    }

    syncBodyThemeClass = (isDarkMode) => {
        if (typeof document === 'undefined') {
            return;
        }
        document.body.classList.toggle('theme-dark', isDarkMode);
    };


    render() {
        const { languageState, darkMode } = this.state;
        const hostname = typeof window !== 'undefined'
            ? String(window.location.hostname || '').toLowerCase()
            : '';
        const isAdminHost = hostname.startsWith('admin.');
        const viewRouteProps = {
            isAdminHost,
            languageState,
            darkMode,
            onViewChange: this.setViewMode,
        };
        return (
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <div className={`wrapper${darkMode ? ' theme-dark' : ''}`}>
                    <div className="headerBar">
                        <div className="headerBarSection1">
                            <div className="logoContainer">
                                <Link to="/">
                                    <Logo style={{ width: '100%', height: '100%' }} />
                                </Link>
                            </div>

                            <div className="logoWrapper">
                                <h2 className="brand-alt">
                                    <a href="/">
                                        hempejl
                                    </a>
                                </h2>
                                <p>
                                    <a href="/">
                                        {languageState.swedish ? 'Rätt bostad i rätt område' : 'Make your move easier'}
                                    </a>
                                </p>
                            </div>
                        </div>
                        <div className="headerBarSection2">
                            <div className="headerQuickToggles">
                                <button type="button" className="languageSwitch" onClick={this.toggleLanguage} aria-label="Toggle language">
                                    <span className={`languageSwitchBtn ${languageState.swedish ? 'is-active' : ''}`}>SV</span>
                                    <span className="languageSwitchSep">/</span>
                                    <span className={`languageSwitchBtn ${!languageState.swedish ? 'is-active' : ''}`}>ENG</span>
                                </button>
                                <button
                                    type="button"
                                    className="themeSwitch"
                                    onClick={this.toggleThemeMode}
                                    aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                                >
                                    {darkMode ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
                                </button>
                                <Link
                                    to="/faq"
                                    className="themeSwitch headerHelpLink"
                                    aria-label={languageState.swedish ? 'Vanliga frågor' : 'Frequently asked questions'}
                                    title={languageState.swedish ? 'Vanliga frågor' : 'FAQ'}
                                >
                                    <HelpOutlineIcon fontSize="small" />
                                </Link>
                            </div>
                            {this.betaVersion &&  <Button variant="text" style={{color:'white'}} startIcon={<PersonIcon style={{color:'white'}}/>} component={Link} to="/login">{languageState.swedish ? 'Logga in' : 'Sign in'}</Button>}

                        </div>
                    </div>
                    < div className="mainContainer">
                        <Routes >
                            <Route path="/" element={<AppViewRoute view="listvy" {...viewRouteProps} />} />
                            <Route path="/kartvy" element={<AppViewRoute view="kartvy" {...viewRouteProps} />} />
                            <Route path="/listvy" element={<Navigate to="/" replace />} />
                            <Route path="/admin" element={<AdminGatePage />} />
                            <Route path="/login" element={<LoginPage/>} />
                            <Route path="/mypages" element={<MyPages/>} />
                            <Route path="/faq" element={<FaqPage languageState={languageState} />} />
                            <Route path="/omrade/:code/*" element={<AreaPage languageState={languageState} />} />
                            <Route path="/jamfor/:codes/*" element={<ComparePage languageState={languageState} />} />
                        </Routes>
                    </div>
                </div>
            </Router>
        );
    }
}
